import {Component, forwardRef, Input} from '@angular/core';
import {CacheDataService} from '../../api/services/cache-data.service';
import {FlatTreeControl} from '@angular/cdk/tree';
import {TreeViewSelectionNode} from './helpers/tree-view-selection-node';
import {TreeViewDataSource} from './helpers/tree-vew-data-source';
import {FilterEditorComponent} from './filter-editor/filter-editor.component';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CleanLocation} from '../../api/models/clean-location';
import {LocationKey} from '../../api/models/location-key';

@Component({
  selector: "location-filter-editor",
  templateUrl: "./filter-editor/filter-editor.component.html",
  styleUrls: ["./filter-editor/filter-editor.component.css"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationFilterEditorComponent),
      multi: true
    }
  ]
})
export class LocationFilterEditorComponent extends FilterEditorComponent<CleanLocation, LocationKey, TVSNCleanLocation, CleanLocationDataSource> implements ControlValueAccessor {

  constructor(cacheDataService: CacheDataService) {
    super(cacheDataService);
  }

  protected getNewDataSource(treeControl: FlatTreeControl<TVSNCleanLocation, TVSNCleanLocation>, cacheDataService: CacheDataService): CleanLocationDataSource {
    return new CleanLocationDataSource(treeControl, cacheDataService);
  }
}


export class CleanLocationDataSource extends TreeViewDataSource<CleanLocation, LocationKey, TVSNCleanLocation> {

  constructor(
    treeControl: FlatTreeControl<TVSNCleanLocation>,
    public cacheDataService: CacheDataService) {
    super(treeControl);
  }

  protected loadInitialData(callback: (results: TVSNCleanLocation[]) => void) {
    this.cacheDataService.cacheDataGetCleanLocationCountries({ pageNumber: 1, pageSize: 1000 }).subscribe(result => {
      const nodes = result.page.map(item => new TVSNCleanLocation(
        [], this.blankCover(item.countryIso), 1,
        {
          countryIso: item.countryIso
        },
        undefined, true, true
      ));
      callback(nodes);
    });
  }

  protected loadChildNodes(node: TVSNCleanLocation, callback: (results: TVSNCleanLocation[]) => void) {
    if (node.level === 1) {
      this.cacheDataService.cacheDataGetCleanLocationRegionsByCountry({ country: node.sourceValue.countryIso, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes : TVSNCleanLocation[] = result.page.map(item => new TVSNCleanLocation(
          [], this.blankCover(item.stateProvinceRegion), node.level + 1,
          {
            countryIso: item.countryIso,
            stateProvinceRegion: item.stateProvinceRegion
          },
          node, true, true
        ));
        callback(nodes);
      });
    } else if (node.level === 2) {
      this.cacheDataService.cacheDataGetCleanLocationsByCountryAndRegion({ country: node.sourceValue.countryIso, region: node.sourceValue.stateProvinceRegion, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes: TVSNCleanLocation[] = result.page.map(item => new TVSNCleanLocation(
          [], this.blankCover(item.city), node.level + 1,
          {
            countryIso: item.countryIso,
            stateProvinceRegion: item.stateProvinceRegion,
            city: item.city
          },
          node, false, true
        ));
        callback(nodes);
      });
    }
  }
}

export class TVSNCleanLocation extends TreeViewSelectionNode<CleanLocation, LocationKey> {

  protected deriveKey(): LocationKey {
    return {
      country: this.sourceValue.countryIso,
      stateProvinceRegion: this.sourceValue.stateProvinceRegion,
      city: this.sourceValue.city
    };
  }

  protected isAnyKnownChildSelected(allKnownSelections: LocationKey[]): boolean {
    // Rules:
    // If city != null, there can be no children
    // If current level matches, it must NOT match entirely (i.e, there target should have a lower level than THIS node

    if (!this.sourceValue.city) {
      if (this.sourceValue.stateProvinceRegion) {
        return allKnownSelections?.some(x => x.stateProvinceRegion == this.sourceValue.stateProvinceRegion && x.city);
      } else if (this.sourceValue.countryIso) {
        return allKnownSelections?.some(x => x.country === this.sourceValue.countryIso && x.stateProvinceRegion);
      }
    }

    return false;
  }

  public isKeyForMe(key: LocationKey): boolean {
    return key.city == this.sourceValue.city
    && key.stateProvinceRegion === this.sourceValue.stateProvinceRegion
    && key.country === this.sourceValue.countryIso;
  }

  public isChildOf(key: LocationKey): boolean {
    if (key.stateProvinceRegion != null) {
      return key.country === this.sourceValue.countryIso
        && key.stateProvinceRegion === this.sourceValue.stateProvinceRegion;
    } else if (key.country != null) {
      return key.country === this.sourceValue.countryIso;
    }

    return false;
  }

  public toSourceKey(): LocationKey {
    return {
      country: this.sourceValue.countryIso,
      stateProvinceRegion: this.sourceValue.stateProvinceRegion,
      city: this.sourceValue.city
    }
  }

  public matchesKey(key: LocationKey): boolean {
    return key.city === this.sourceValue.city
    && key.stateProvinceRegion === this.sourceValue.stateProvinceRegion
    && key.country === this.sourceValue.countryIso;
  }

}
